import React, { useEffect, useState, useMemo } from "react";
import { FolderOpen, Download, FileText, ChevronDown, ChevronRight, Calendar, Users, ClipboardCheck } from "lucide-react";
import { getAllStudents } from "../../services/StudentRecordService";
import {
  downloadCourseSheet,
  downloadCampusZip,
} from "../../services/AttendanceTemplateService";
import { getAllCourses } from "../../services/CourseService";
import RecordAttendanceTab from "./RecordAttendanceTab";

// ─── Constants ────────────────────────────────────────────────────────────────
const CAMPUSES_LIST = ["Canberra", "Melbourne"];

const THREEDAYS_TEMPLATE_PATH = "/templates/104 weeks CPC30220 - Attendance Sheet 2026 Term 1.docx";
const FIVEDAYS_TEMPLATE_PATH  = "/templates/52 weeks CPC30220 - Attendance Sheet 2026 Term 1.docx";
const OEG_TEMPLATE_PATH       = "/templates/OEG General English Attendance Sheet.docx";
const TCMT_TEMPLATE_PATH      = "/templates/Melbourne Attendance Sheet-Advance diploma in civil construction design.docx";

// ─── Fixed-template courses (bypass COE/VOE and 3/5-day split) ───────────────
const FIXED_TEMPLATE_COURSES = [
  { match: "general english",                        path: OEG_TEMPLATE_PATH  },
  { match: "advanced diploma of civil construction", path: TCMT_TEMPLATE_PATH },
  { match: "advance diploma of civil construction",  path: TCMT_TEMPLATE_PATH },
];

const COURSE_CODE_MAP = {
  "Certificate III in Bricklaying and Blocklaying ":                    "CPC33020",
  "Certificate III in Bricklaying and Blocklaying":                    "CPC33020",
  "Certificate III in Carpentry":                                      "CPC30220",
  "Certificate III in Carpentry(New)":                                 "CPC30326",
  "Certificate III in Painting and Decorating":                        "CPC30620",
  "Certificate III in Commercial Cookery":                             "SIT30821",
  "Certificate III in Individual Support":                             "CHC33021",
  "Certificate IV in Building and Construction(Building)":             "CPC40120",
  "Certificate IV in Kitchen Management":                              "SIT40521",
  "Certificate IV in Disability Support":                              "CHC43121",
  "Diploma of Building and Construction(Building)":                    "CPC50220",
  "Diploma of Information Technology( Cyber Security)":                "ICT50220",
  "Diploma of Hospitality Management":                                 "SIT50422",
  "Diploma of Leadership and Management":                              "BSB50420",
  "Diploma of Community Services":                                     "CHC52025",
  "Advanced Diploma of Information Technology(Cyber Security/Telecommunication)": "ICT60220",
  "Advanced Diploma of Hospitality Management":                        "SIT60322",
  "Advanced Diploma of Leadership and Management":                     "BSB60420",
  "Advanced Diploma of Building and Construction Management":          "CPC60220",
  "Advanced Diploma of Civil Construction Design":                     "RII60520",
  "Graduate Diploma of Management(Learning)":                          "BSB80120",
  "General English":                                                   "118510J",
  "English for Academic Prepartion":                                   "EAP",
  "11084NAT Asbestos Awareness":                                       "11084NAT",
  "10830NAT Silica Awareness":                                         "10830NAT",
  "CPCWHS1001 White Card":                                             "CPCWHS1001",
};
const ALL_CPC_COURSES = Object.keys(COURSE_CODE_MAP).filter(name =>
  COURSE_CODE_MAP[name]?.startsWith("CPC")
);

// Helper: returns "CODE CourseName" or just "CourseName" if no code found
function withCourseCode(courseName) {
  const code = COURSE_CODE_MAP[courseName];
  return code ? `${code} ${courseName}` : courseName;
}

function resolveTemplatePath(courseName, classPerWeek) {
  const lower = (courseName || "").toLowerCase();
  const fixed = FIXED_TEMPLATE_COURSES.find(({ match }) => lower.includes(match));
  if (fixed) return fixed.path;
  return (classPerWeek || "").toLowerCase().includes("5")
    ? FIVEDAYS_TEMPLATE_PATH
    : THREEDAYS_TEMPLATE_PATH;
}

function isFixedTemplateCourse(courseName) {
  const lower = (courseName || "").toLowerCase();
  return FIXED_TEMPLATE_COURSES.some(({ match }) => lower.includes(match));
}

// ─── Split into all 4 buckets: COE×3day | COE×5day | VOE×3day | VOE×5day ─────
function splitStudents(studentList) {
  const isFive = (s) => (s.classPerWeek || "").toLowerCase().includes("5");
  const isCOE  = (s) => (s.enrolmentType || "").trim().toUpperCase() === "COE";
  const isVOE  = (s) => (s.enrolmentType || "").trim().toUpperCase() === "VOE";

  const isReal = (s) => (s.studentName || "").trim() !== "";

  return {
    coeThreeDays: studentList.filter(s => isCOE(s) && !isFive(s) && isReal(s)),
    coeFiveDays:  studentList.filter(s => isCOE(s) &&  isFive(s) && isReal(s)),
    voeThreeDays: studentList.filter(s => isVOE(s) && !isFive(s) && isReal(s)),
    voeFiveDays:  studentList.filter(s => isVOE(s) &&  isFive(s) && isReal(s)),
  };
}
function splitStudentsForTemplate(studentList) {
  const isFive = (s) => (s.classPerWeek || "").toLowerCase().includes("5");
  const isCOE  = (s) => (s.enrolmentType || "").trim().toUpperCase() === "COE";
  const isVOE  = (s) => (s.enrolmentType || "").trim().toUpperCase() === "VOE";

  return {
    coeThreeDays: studentList.filter(s => isCOE(s) && !isFive(s)),
    coeFiveDays:  studentList.filter(s => isCOE(s) &&  isFive(s)),
    voeThreeDays: studentList.filter(s => isVOE(s) && !isFive(s)),
    voeFiveDays:  studentList.filter(s => isVOE(s) &&  isFive(s)),
  };
}

// ─── Reusable download button ─────────────────────────────────────────────────
function DlButton({ label, count, dlKey, downloading, onClick, activeColor }) {
  const active    = count > 0;
  const isLoading = downloading === dlKey;
  return (
    <button
      onClick={active && !downloading ? onClick : undefined}
      disabled={!active || !!downloading}
      title={active ? `${count} student${count !== 1 ? "s" : ""}` : "No students"}
      style={{
        display: "flex", alignItems: "center", gap: 4,
        padding: "5px 9px",
        background: active ? activeColor : "#f1f5f9",
        color:      active ? "#fff"      : "#94a3b8",
        border:    `1px solid ${active ? activeColor : "#e2e8f0"}`,
        borderRadius: 5,
        cursor: active && !downloading ? "pointer" : "not-allowed",
        fontSize: "0.75rem", fontWeight: 600, whiteSpace: "nowrap",
        opacity: isLoading ? 0.6 : 1,
        transition: "opacity 0.15s",
      }}
    >
      <Download size={11} />
      {isLoading ? "…" : `${label} (${count})`}
    </button>
  );
}

// ─── Date helpers ─────────────────────────────────────────────────────────────
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric" }) : "";

const getCurrentWeekDays = () => {
  const now = new Date();
  const day = now.getDay();
  const diff = day === 0 ? -6 : 1 - day;

  const mon = new Date(now);
  mon.setDate(now.getDate() + diff);
  mon.setHours(0, 0, 0, 0);

  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(mon);
    d.setDate(mon.getDate() + i);
    return d;
  });
};

// ─── Build ZIP map: templatePath → { courseVariantName: studentList } ─────────
function buildByTemplate(courseMap) {
  const byTemplate = {};
  const add = (tPath, key, students) => {
    if (!byTemplate[tPath]) byTemplate[tPath] = {};
    byTemplate[tPath][key] = students;
  };
  Object.entries(courseMap).forEach(([courseName, studentList]) => {
    // Use code-prefixed name as the key inside each template bucket
    const displayName = withCourseCode(courseName);

    if (isFixedTemplateCourse(courseName)) {
      add(resolveTemplatePath(courseName, ""), displayName, studentList);
    } else {
      const { coeThreeDays, coeFiveDays, voeThreeDays, voeFiveDays } = splitStudents(studentList);
      if (coeThreeDays.length > 0) add(THREEDAYS_TEMPLATE_PATH, ` ${displayName}`, coeThreeDays);
      if (coeFiveDays.length  > 0) add(FIVEDAYS_TEMPLATE_PATH,  ` ${displayName}`, coeFiveDays);
      if (voeThreeDays.length > 0) add(THREEDAYS_TEMPLATE_PATH, ` ${displayName}`, voeThreeDays);
      if (voeFiveDays.length  > 0) add(FIVEDAYS_TEMPLATE_PATH,  ` ${displayName}`, voeFiveDays);
    }
  });
  return byTemplate;
}

// ─── Main Component ───────────────────────────────────────────────────────────
function AttendanceDownloadsTab() {
  const [students,    setStudents]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [openCampus,  setOpenCampus]  = useState({ Canberra: true, Melbourne: false });
  const [downloading, setDownloading] = useState(null);
  const [error,       setError]       = useState(null);
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    getAllStudents()
      .then((data) => setStudents(data || []))
      .catch((err) => {
        setError("Could not load student records. Please refresh and try again.");
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    Promise.all([getAllStudents(), getAllCourses()])
      .then(([studentData, courseData]) => {
        setStudents(studentData || []);
        setCourses(courseData || []);
      })
      .catch((err) => {
        console.error(err);
        setError("Could not load student/course records. Please refresh and try again.");
      })
      .finally(() => setLoading(false));
  }, []);
  const getCourseAttendanceDetails = (courseName, campus, days) => {
    const clean = (v) => (v || "").toString().trim().toLowerCase();

    const matchedCourse = courses.find((c) => {
      return (
        clean(c.courseName) === clean(courseName) ||
        clean(withCourseCode(c.courseName)) === clean(courseName) ||
        clean(c.courseCode + " " + c.courseName) === clean(courseName)
      );
    });

    const campusDetails = matchedCourse?.attendanceDetails?.[campus] || {};

    const scheduleKey = days === "5" ? "fiveDays" : "threeDays";

    // New mapping
    if (campusDetails?.[scheduleKey]) {
      return campusDetails[scheduleKey];
    }

    // Old mapping fallback
    return campusDetails || {};
  };

  // ── Grouping ───────────────────────────────────────────────────────────────
  const grouped = useMemo(() => {
    const weekDays = getCurrentWeekDays();
    const monday   = weekDays[0];
    const sunday   = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);

    const result = { Canberra: {}, Melbourne: {} };

    const includedStatuses = [
      "ongoing",
    ];

    students.forEach((student) => {
      const f         = student?.formState || {};
      const campus    = (f.campusName || "").trim();
      const status    = (f.studentStatus || "").toLowerCase();
      const campusKey = CAMPUSES_LIST.find((c) =>
        campus.toLowerCase().includes(c.toLowerCase())
      );

      if (!campusKey) return;
      if (!includedStatuses.some(s => status.includes(s))) return;
      if (f.studentId === "") return;

      (f.courses || []).forEach((course) => {
        if (!course.courseStartDate || !course.courseEndDate) return;

        const start     = new Date(course.courseStartDate);
        const end       = new Date(course.courseEndDate);
        const coeStatus = (course.coeStatus || "").toLowerCase();

        if (start > sunday || end < monday) return;
        if (coeStatus === "cancelled") return;

        const name = course.courseName || "Unknown Course";
        if (!result[campusKey][name]) result[campusKey][name] = [];

        result[campusKey][name].push({
          studentId:     f.studentId    || "",
          studentName:   `${f.firstName || ""} ${f.lastName || ""}`.trim(),
          classPerWeek:  course.classPerWeek || "3 days",
          enrolmentType: f.enrolmentType || "COE",
        });
        
      });
    });
    // adding 5 rows empty for CPC courses
    Object.keys(result).forEach((campusKey) => {
      Object.keys(result[campusKey]).forEach((courseName) => {
        const studentsArr = result[campusKey][courseName];

        const hasVOE = studentsArr.some(
          s => (s.enrolmentType || "").toUpperCase() === "VOE" && s.classPerWeek === "5 days"
        );

        const hasCOE = studentsArr.some(
          s => (s.enrolmentType || "").toUpperCase() === "COE" && s.classPerWeek === "5 days"
        );

        let typeToAdd = null;

        if (hasVOE) {
          typeToAdd = "VOE";
          for (let i = 0; i < 5; i++) {
            studentsArr.push({
              studentId: " ",
              studentName: " ",
              classPerWeek: "5 days",
              enrolmentType: typeToAdd,
            });
          }
        }  
        if (hasCOE) {
          typeToAdd = "COE";
          for (let i = 0; i < 5; i++) {
            studentsArr.push({
              studentId: " ",
              studentName: " ",
              classPerWeek: "5 days",
              enrolmentType: typeToAdd,
            });
          }
        }
        
      });
    });
    
    return result;
  }, [students]);

  // ── Week label + tag ───────────────────────────────────────────────────────
  const weekDays = getCurrentWeekDays();

  const weekLabel = `${weekDays[0].toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "short",
  })} – ${weekDays[6].toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })}`;
  
  const COURSE_ATTENDANCE_DETAILS = {
    Canberra: {
      "Certificate III in Carpentry": {
        group: "CPC30220-CBR-G1",
        unitName: "CPC30220 Certificate III in Carpentry",
        trainer: "Trainer Name",
        room: "Room 1",
        block: "Block A",
        term: "Term 1",
        year: "2026",
        week: "02",
        day1: "Mon 9:00 AM - 5:00 PM",
        day2: "Tue 9:00 AM - 5:00 PM",
        day3: "Wed 9:00 AM - 5:00 PM",
        totalHours: "20hr / 1hr",
      },
    },

    Melbourne: {
      "Certificate III in Carpentry": {
        group: "CPC30220-MEL-G1",
        unitName: "CPC30220 Certificate III in Carpentry",
        trainer: "Trainer Name",
        room: "Room 2",
        block: "Block B",
        term: "Term 1",
        year: "2026",
        week: "02",
        day1: "9:00 AM - 5:00 PM / 30 mins",
        day2: "Tue 9:00 AM - 5:00 PM",
        day3: "Wed 9:00 AM - 5:00 PM",
        totalHours: "20hr / 1hr",
      },
    },
  };

  const weekStart = fmtDate(weekDays[0]);
  const weekEnd = fmtDate(weekDays[6]);
  const weekTag = weekStart.replace(/\//g, "-");
  // ── Download wrapper ───────────────────────────────────────────────────────
  const withDownload = (key, fn) => async () => {
    setDownloading(key);
    setError(null);
    try {
      await fn();
    } catch (err) {
      console.error("Download error:", err);
      setError(
        err.message?.includes("Failed to load template") || err.message?.includes("not found")
          ? "Template file not found. Check /public/templates/ folder."
          : "Failed to generate document. Check the console for details."
      );
    } finally {
      setDownloading(null);
    }
  };

  // ── Download single course sheet ───────────────────────────────────────────
  const handleDownloadCourse = (campus, courseName, days, enrolmentType) => {
    const all   = grouped[campus][courseName];
    const fixed = isFixedTemplateCourse(courseName);

    let studentList;
    if (fixed) {
      studentList = all;
    } else {
      const split = splitStudentsForTemplate(all);
      if      (enrolmentType === "COE" && days === "3") studentList = split.coeThreeDays;
      else if (enrolmentType === "COE" && days === "5") studentList = split.coeFiveDays;
      else if (enrolmentType === "VOE" && days === "3") studentList = split.voeThreeDays;
      else if (enrolmentType === "VOE" && days === "5") studentList = split.voeFiveDays;
      else studentList = all;
    }

    if (!studentList || studentList.length === 0) return;

    const templatePath = resolveTemplatePath(courseName, days === "5" ? "5 days" : "3 days");
    const dlKey        = fixed ? `${campus}-${courseName}` : `${campus}-${courseName}-${enrolmentType}-${days}`;
    const suffix       = fixed ? "" : `_${enrolmentType}_${days}days`;

    const courseRecord = students
      .flatMap((s) => s.formState?.courses || [])
      .find((c) => c.courseName === courseName);

    const details = getCourseAttendanceDetails(courseName, campus, days);
    const weekInput = window.prompt("Enter week number:", "");
    if (weekInput === null) return;

    const unitNameInput = window.prompt(
      "Enter Unit Code and Name:",
      withCourseCode(courseName)
    );
    if (unitNameInput === null) return;

    withDownload(dlKey, () =>
      downloadCourseSheet({
        templatePath,
        courseName: withCourseCode(courseName),   // ← new: "CODE CourseName" goes into [[UNIT_CODE]]
        studentList,
        filename: `attendance_${campus}_${withCourseCode(courseName).slice(0, 35)}${suffix}_${weekTag}`,

        attendanceDetails: {
          UNIT_CODE: COURSE_CODE_MAP[courseName] + " " + courseName || "",
          a_g: details.group || "",
          a_unit_name: unitNameInput  || withCourseCode(courseName),
          a_w: weekInput || weekLabel,
          a_trainer: details.trainer || "",
          w_s: weekStart,
          w_e: weekEnd,
          a_r: details.room || "",
          a_b: campus || "",
          a_t: details.term || "",
          a_y: new Date().getFullYear(),
          a_d1: details.day1 || "",
          a_d2: details.day2 || "",
          a_d3: details.day3 || "",
          a_d4: details.day4 || "",
          a_d5: details.day5 || "",
          totalHours: details.totalHours || "20hr / 1hr",
        },
      })
    )();
  };

  // ── Download all courses for one campus ───────────────────────────────────
  const handleDownloadCampus = (campus) => {
    const courseMap = grouped[campus] || {};
    if (!Object.keys(courseMap).length) {
      setError(`No active students for ${campus} this week.`);
      return;
    }

    withDownload(`all-${campus}`, async () => {
      const byTemplate = buildByTemplate(courseMap);
      const templateLabels = {
        [THREEDAYS_TEMPLATE_PATH]: `${campus}_3days`,
        [FIVEDAYS_TEMPLATE_PATH]:  `${campus}_5days`,
        [OEG_TEMPLATE_PATH]:       `${campus}_OEG`,
        [TCMT_TEMPLATE_PATH]:      `${campus}_TCMT`,
      };
      await Promise.all(
        Object.entries(byTemplate).map(([tPath, courseMapSlice]) =>
          downloadCampusZip({
            templatePath: tPath,
            campus:       templateLabels[tPath] || campus,
            courseMap:    courseMapSlice,
            weekTag,
          })
        )
      );
    })();
  };

  // ── Download ALL campuses at once ──────────────────────────────────────────
  const handleDownloadAll = () => {
    const hasAny = CAMPUSES_LIST.some((c) => Object.keys(grouped[c] || {}).length > 0);
    if (!hasAny) { setError("No active students with courses this week."); return; }

    withDownload("all", async () => {
      const allDownloads = [];

      CAMPUSES_LIST
        .filter((c) => Object.keys(grouped[c] || {}).length > 0)
        .forEach((campus) => {
          const byTemplate = buildByTemplate(grouped[campus]);
          const templateLabels = {
            [THREEDAYS_TEMPLATE_PATH]: `${campus}_3days`,
            [FIVEDAYS_TEMPLATE_PATH]:  `${campus}_5days`,
            [OEG_TEMPLATE_PATH]:       `${campus}_OEG`,
            [TCMT_TEMPLATE_PATH]:      `${campus}_TCMT`,
          };
          Object.entries(byTemplate).forEach(([tPath, courseMapSlice]) => {
            allDownloads.push(
              downloadCampusZip({
                templatePath: tPath,
                campus:       templateLabels[tPath] || campus,
                courseMap:    courseMapSlice,
                weekTag,
              })
            );
          });
        });

      await Promise.all(allDownloads);
    })();
  };

  // ── Print all sheets for a campus ─────────────────────────────────────────
  const handlePrintAll = (campus) => {
    const courseMap = grouped[campus] || {};
    if (!Object.keys(courseMap).length) {
      setError(`No active students for ${campus} this week.`);
      return;
    }

    const dayLabels = weekDays
      .slice(0, 3)
      .map(d => d.toLocaleDateString("en-AU", { weekday: "long", day: "2-digit", month: "short" }));

    // Build one print page per variant
    const pages = [];
    Object.entries(courseMap).forEach(([courseName, studentList]) => {
      if (isFixedTemplateCourse(courseName)) {
        pages.push({ courseName, label: "", students: studentList });
      } else {
        const { coeThreeDays, coeFiveDays, voeThreeDays, voeFiveDays } = splitStudents(studentList);
        if (coeThreeDays.length > 0) pages.push({ courseName, label: "COE · 3 Days", students: coeThreeDays });
        if (coeFiveDays.length  > 0) pages.push({ courseName, label: "COE · 5 Days", students: coeFiveDays  });
        if (voeThreeDays.length > 0) pages.push({ courseName, label: "VOE · 3 Days", students: voeThreeDays });
        if (voeFiveDays.length  > 0) pages.push({ courseName, label: "VOE · 5 Days", students: voeFiveDays  });
      }
    });

    const sheets = pages.map(({ courseName, label, students }) => {
      const rows = students.map((s, i) => `
        <tr>
          <td class="c">${i + 1}</td>
          <td>${s.studentId}</td>
          <td>${s.studentName}</td>
          ${[0,1,2].map(() => `<td></td><td class="sig"></td><td></td><td class="sig"></td>`).join("")}
          <td></td><td></td>
        </tr>`).join("");

      return `
        <div class="sheet">
          <div class="hdr">
            <h1>Canberra Institute of Education and Technology</h1>
            <p>CRICOS Provider Code: 03835K | RTO Code: 45592</p>
          </div>
          <div class="title">Attendance Sheet${label ? ` — ${label}` : ""}</div>
          <table class="meta">
            <tr><td class="lbl">Unit Code and Name</td><td colspan="3">${courseName}</td></tr>
            <tr>
              <td class="lbl">Week Starting</td><td>${weekLabel.split("–")[0].trim()}</td>
              <td class="lbl">Week Ending</td><td>${(weekLabel.split("–")[1]||"").trim()}</td>
            </tr>
            <tr><td class="lbl">Name of Trainer</td><td></td><td class="lbl">Room Number</td><td></td></tr>
            <tr><td class="lbl">Campus</td><td>${campus}</td><td class="lbl">Term / Year</td><td></td></tr>
            <tr><td class="lbl">Class Timing / Break Time</td><td colspan="3"></td></tr>
          </table>
          <table class="att">
            <thead>
              <tr>
                <th rowspan="3" style="width:3%">No</th>
                <th rowspan="3" style="width:9%">Student ID</th>
                <th rowspan="3" style="width:11%">Student Name</th>
                <th colspan="4">Day 1 — ${dayLabels[0]}</th>
                <th colspan="4">Day 2 — ${dayLabels[1]}</th>
                <th colspan="4">Day 3 — ${dayLabels[2]}</th>
                <th rowspan="3" style="width:5%">Total Hours</th>
                <th rowspan="3" style="width:7%">Notes</th>
              </tr>
              <tr>
                <th colspan="4">Class Time</th>
                <th colspan="4">Class Time</th>
                <th colspan="4">Class Time</th>
              </tr>
              <tr>
                ${[0,1,2].map(()=>`<th>Sign-in</th><th class="sig">Signature</th><th>Sign-out</th><th class="sig">Signature</th>`).join("")}
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
          <table class="ft">
            <tr>
              <td class="lbl" style="width:14%">Trainer Name</td><td style="width:22%"></td>
              <td class="lbl" style="width:14%">Trainer Signature</td><td style="width:22%"></td>
              <td class="lbl" style="width:8%">Date</td><td></td>
            </tr>
            <tr><td class="lbl">Entered in System by:</td><td colspan="5"></td></tr>
          </table>
          <div class="addr">
            <strong>CANBERRA INSTITUTE OF EDUCATION AND TECHNOLOGY</strong><br/>
            Ground Floor 15 BARRY DRIVE, TURNER ACT 2612 | CRICOS NO. 03835K<br/>
            Telephone: 0261703300 · Email: info@ciet.edu.au · Website: www.ciet.edu.au
          </div>
        </div>`;
    }).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Attendance — ${campus} — ${weekLabel}</title>
    <style>
      @page { size: A4 landscape; margin: 7mm; }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: "Times New Roman", serif; font-size: 8.5pt; }
      .sheet { page-break-after: always; }
      .sheet:last-child { page-break-after: avoid; }
      .hdr { text-align: center; margin-bottom: 3px; }
      .hdr h1 { color: #C00000; font-size: 13pt; font-weight: bold; }
      .hdr p  { font-size: 8.5pt; }
      .title  { text-align: center; font-weight: bold; font-size: 11pt; border: 1px solid #000; padding: 3px; margin: 3px 0; }
      table   { border-collapse: collapse; width: 100%; margin-bottom: 3px; }
      td, th  { border: 1px solid #000; padding: 2px 3px; vertical-align: middle; font-size: 7.5pt; }
      th      { text-align: center; font-weight: bold; }
      .meta .lbl { font-weight: bold; white-space: nowrap; background: #f0f0f0; }
      .att tbody tr td { height: 18mm; }
      .sig    { min-width: 16mm; }
      .c      { text-align: center; }
      .ft .lbl { font-weight: bold; background: #f0f0f0; white-space: nowrap; }
      .ft td  { height: 9mm; }
      .addr   { text-align: center; font-size: 7pt; border-top: 1px solid #000; padding-top: 3px; margin-top: 3px; }
      @media print { body { -webkit-print-color-adjust: exact; } }
    </style>
    </head><body>
    ${sheets}
    <script>
      window.onload = () => { window.print(); window.onafterprint = () => window.close(); };
    </script>
    </body></html>`;

    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
  };

  // ─── UI ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "#666" }}>
        Loading attendance data…
      </div>
    );
  }

  return (
    <div style={{ margin: "0 auto", padding: "1.5rem 1rem" }}>

      {/* ── Top bar ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem", flexWrap: "wrap", gap: "1rem" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 700, color: "#1e293b" }}>
            Weekly Attendance Sheets
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: "0.88rem", color: "#64748b" }}>
            Week: <strong>{weekLabel}</strong> · Active students · Courses running this week
          </p>
          {/* Legend */}
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            {[
              { label: "COE · 3 Days", bg: "#f0fdf4", color: "#166534", border: "#bbf7d0" },
              { label: "COE · 5 Days", bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
              { label: "VOE · 3 Days", bg: "#fefce8", color: "#854d0e", border: "#fde68a" },
              { label: "VOE · 5 Days", bg: "#fff7ed", color: "#c46b05", border: "#fed7aa" },
              { label: "Fixed Template", bg: "#f5f3ff", color: "#7c3aed", border: "#ddd6fe" },
            ].map(({ label, bg, color, border }) => (
              <span key={label} style={{
                display: "flex", alignItems: "center", gap: 4,
                fontSize: "0.72rem", fontWeight: 600, color,
                background: bg, border: `1px solid ${border}`,
                borderRadius: 20, padding: "2px 9px",
              }}>
                <Calendar size={10} /> {label}
              </span>
            ))}
          </div>
        </div>

        {/* <button
          onClick={handleDownloadAll}
          disabled={!!downloading}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "8px 16px", background: "#056ec4", color: "#fff",
            border: "none", borderRadius: 6, cursor: "pointer",
            fontWeight: 600, fontSize: "0.88rem",
            opacity: downloading === "all" ? 0.7 : 1,
          }}
        >
          <Download size={15} />
          {downloading === "all" ? "Generating…" : "Download All Campuses"}
        </button> */}
      </div>

      {/* ── Error banner ── */}
      {error && (
        <div style={{
          marginBottom: "1rem", padding: "10px 14px",
          background: "#fef2f2", border: "1px solid #fca5a5",
          borderRadius: 8, color: "#b91c1c", fontSize: "0.88rem",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          {error}
          <button onClick={() => setError(null)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#b91c1c", fontWeight: 700, fontSize: "1rem" }}>
            ×
          </button>
        </div>
      )}

      {/* ── Campus folders ── */}
      {CAMPUSES_LIST.map((campus) => {
        const courseMap     = grouped[campus] || {};
        const courseCount   = Object.keys(courseMap).length;
        const totalStudents = Object.values(courseMap).reduce(
          (total, studentList) =>
            total + studentList.filter((s) => (s.studentName || "").trim() !== "").length,
          0
        );
        const isOpen        = !!openCampus[campus];

        return (
          <div key={campus} style={{
            marginBottom: "1rem", border: "1px solid #e2e8f0",
            borderRadius: 10, overflow: "hidden",
            boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
          }}>
            {/* Campus header */}
            <div
              onClick={() => setOpenCampus((p) => ({ ...p, [campus]: !p[campus] }))}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "13px 18px",
                background: isOpen ? "#f0f7ff" : "#f8fafc",
                cursor: "pointer",
                borderBottom: isOpen ? "1px solid #e2e8f0" : "none",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {isOpen ? <ChevronDown size={17} color="#056ec4" /> : <ChevronRight size={17} color="#94a3b8" />}
                <FolderOpen size={20} color={isOpen ? "#056ec4" : "#94a3b8"} />
                <span style={{ fontWeight: 700, fontSize: "0.97rem", color: "#1e293b" }}>{campus}</span>
                <span style={{ fontSize: "0.76rem", color: "#64748b", background: "#e2e8f0", borderRadius: 20, padding: "2px 8px" }}>
                  {courseCount} course{courseCount !== 1 ? "s" : ""} · {totalStudents} student{totalStudents !== 1 ? "s" : ""}
                </span>
              </div>

              {/* Campus-level buttons */}
              {courseCount > 0 && (
                <div style={{ display: "flex", gap: 6 }} onClick={e => e.stopPropagation()}>
                  
                  {/* <button
                    onClick={() => handleDownloadCampus(campus)}
                    disabled={!!downloading}
                    style={{
                      display: "flex", alignItems: "center", gap: 5,
                      padding: "5px 11px", background: "#056ec4", color: "#fff",
                      border: "none", borderRadius: 5, cursor: "pointer",
                      fontSize: "0.8rem", fontWeight: 600,
                      opacity: downloading === `all-${campus}` ? 0.7 : 1,
                    }}
                  >
                    <Download size={13} />
                    {downloading === `all-${campus}` ? "…" : `All ${campus} (ZIP)`}
                  </button> */}
                </div>
              )}
            </div>

            {/* Course rows */}
            {isOpen && (
              <div>
                {courseCount === 0 ? (
                  <div style={{ padding: "20px 24px", color: "#94a3b8", fontSize: "0.88rem", textAlign: "center" }}>
                    No active students with courses running this week for {campus}.
                  </div>
                ) : (
                  Object.entries(courseMap).map(([courseName, studentList], idx) => {
                    const key    = `${campus}-${courseName}`;
                    const isLast = idx === Object.keys(courseMap).length - 1;
                    const fixed  = isFixedTemplateCourse(courseName);
                    const { coeThreeDays, coeFiveDays, voeThreeDays, voeFiveDays } = splitStudents(studentList);

                    return (
                      <div
                        key={courseName}
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          padding: "12px 22px",
                          borderBottom: isLast ? "none" : "1px solid #f1f5f9",
                          background: "#fff", transition: "background 0.1s",
                          gap: 12, flexWrap: "wrap",
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = "#fafbff"}
                        onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                      >
                        {/* Left — course name + count badges */}
                        <div style={{ display: "flex", alignItems: "center", gap: 11, flex: "1 1 220px", minWidth: 0 }}>
                          <FileText size={17} color={fixed ? "#7c3aed" : "#0a8f5c"} style={{ flexShrink: 0 }} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: "0.9rem", color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {courseName}
                            </div>
                            <div style={{ display: "flex", gap: 5, marginTop: 4, flexWrap: "wrap" }}>
                              {fixed ? (
                                <span style={{ display:"flex",alignItems:"center",gap:3,fontSize:"0.72rem",fontWeight:600,background:"#f5f3ff",color:"#7c3aed",border:"1px solid #ddd6fe",borderRadius:20,padding:"2px 8px" }}>
                                  <Users size={10}/> {studentList.length} student{studentList.length!==1?"s":""} · fixed template
                                </span>
                              ) : (
                                <>
                                  <span style={{ display:"flex",alignItems:"center",gap:3,fontSize:"0.72rem",fontWeight:600,background:coeThreeDays.length>0?"#f0fdf4":"#f8fafc",color:coeThreeDays.length>0?"#166534":"#94a3b8",border:`1px solid ${coeThreeDays.length>0?"#bbf7d0":"#e2e8f0"}`,borderRadius:20,padding:"2px 8px" }}>
                                    <Users size={10}/> COE 3d: {coeThreeDays.length}
                                  </span>
                                  <span style={{ display:"flex",alignItems:"center",gap:3,fontSize:"0.72rem",fontWeight:600,background:coeFiveDays.length>0?"#eff6ff":"#f8fafc",color:coeFiveDays.length>0?"#1d4ed8":"#94a3b8",border:`1px solid ${coeFiveDays.length>0?"#bfdbfe":"#e2e8f0"}`,borderRadius:20,padding:"2px 8px" }}>
                                    <Users size={10}/> COE 5d: {coeFiveDays.length}
                                  </span>
                                  <span style={{ display:"flex",alignItems:"center",gap:3,fontSize:"0.72rem",fontWeight:600,background:voeThreeDays.length>0?"#fefce8":"#f8fafc",color:voeThreeDays.length>0?"#854d0e":"#94a3b8",border:`1px solid ${voeThreeDays.length>0?"#fde68a":"#e2e8f0"}`,borderRadius:20,padding:"2px 8px" }}>
                                    <Users size={10}/> VOE 3d: {voeThreeDays.length}
                                  </span>
                                  <span style={{ display:"flex",alignItems:"center",gap:3,fontSize:"0.72rem",fontWeight:600,background:voeFiveDays.length>0?"#fff7ed":"#f8fafc",color:voeFiveDays.length>0?"#c46b05":"#94a3b8",border:`1px solid ${voeFiveDays.length>0?"#fed7aa":"#e2e8f0"}`,borderRadius:20,padding:"2px 8px" }}>
                                    <Users size={10}/> VOE 5d: {voeFiveDays.length}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Right — student pills + download buttons */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>

                          {/* Student pills */}
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", maxWidth: 200, justifyContent: "flex-end" }}>
                            {studentList.slice(0, 2).map((s) => (
                              <span key={s.studentId} style={{
                                fontSize: "0.7rem", background: "#f0f7ff", color: "#056ec4",
                                border: "1px solid #bfdbfe", borderRadius: 20, padding: "2px 7px", whiteSpace: "nowrap",
                              }}>
                                {s.studentName}
                              </span>
                            ))}
                            {studentList.length > 2 && (
                              <span style={{ fontSize: "0.7rem", background: "#f1f5f9", color: "#64748b", borderRadius: 20, padding: "2px 7px" }}>
                                +{studentList.length - 2} more
                              </span>
                            )}
                          </div>

                          {/* Download buttons */}
                          {fixed ? (
                            <DlButton
                              label="Download" count={studentList.length}
                              dlKey={key} downloading={downloading}
                              onClick={() => handleDownloadCourse(campus, courseName, null, null)}
                              activeColor="#7c3aed"
                            />
                          ) : (
                            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", justifyContent: "flex-end" }}>
                              <DlButton label="COE 3d" count={coeThreeDays.length} dlKey={`${key}-COE-3`}
                                downloading={downloading} activeColor="#0a8f5c"
                                onClick={() => handleDownloadCourse(campus, courseName, "3", "COE")} />
                              <DlButton label="COE 5d" count={coeFiveDays.length}  dlKey={`${key}-COE-5`}
                                downloading={downloading} activeColor="#056ec4"
                                onClick={() => handleDownloadCourse(campus, courseName, "5", "COE")} />
                              <DlButton label="VOE 3d" count={voeThreeDays.length} dlKey={`${key}-VOE-3`}
                                downloading={downloading} activeColor="#854d0e"
                                onClick={() => handleDownloadCourse(campus, courseName, "3", "VOE")} />
                              <DlButton label="VOE 5d" count={voeFiveDays.length}  dlKey={`${key}-VOE-5`}
                                downloading={downloading} activeColor="#c46b05"
                                onClick={() => handleDownloadCourse(campus, courseName, "5", "VOE")} />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Attendance module wrapper ────────────────────────────────────────────────
// The existing download component above remains unchanged and is selected first.
export default function AttendanceSection() {
  const [activeTab, setActiveTab] = useState("downloads");

  return (
    <div>
      <div
        style={{
          display: "flex",
          gap: 6,
          padding: "1rem 1rem 0",
          borderBottom: "1px solid #e2e8f0",
          background: "#fff",
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          onClick={() => setActiveTab("downloads")}
          style={topTabStyle(activeTab === "downloads")}
        >
          <Download size={15} />
          Attendance Downloads
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("records")}
          style={topTabStyle(activeTab === "records")}
        >
          <ClipboardCheck size={15} />
          Record Attendance
        </button>
      </div>

      {activeTab === "downloads" ? (
        <AttendanceDownloadsTab />
      ) : (
        <RecordAttendanceTab courseCodeMap={COURSE_CODE_MAP} />
      )}
    </div>
  );
}

function topTabStyle(active) {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "9px 13px",
    border: "none",
    borderBottom: active ? "3px solid #056ec4" : "3px solid transparent",
    background: "transparent",
    color: active ? "#056ec4" : "#64748b",
    cursor: "pointer",
    fontSize: "0.84rem",
    fontWeight: 700,
  };
}